import { useEffect, useState } from 'react'
import { Plus, Trash2, Edit2, Package, Check, X } from 'lucide-react'
import { inventoryApi, type InventoryItem } from '../services/api'
import IngredientAutocomplete from '../components/IngredientAutocomplete'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/useAuth'
import AccessDenied from '../components/AccessDenied'

export default function Inventory() {
    const [items, setItems] = useState<InventoryItem[]>([])
    const [loading, setLoading] = useState(true)
    const [showAdd, setShowAdd] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [showAccessDenied, setShowAccessDenied] = useState(false)
    const { isAuthenticated, loading: authLoading } = useAuth()

    // Form state
    const [form, setForm] = useState({ ingredient_name: '', quantity: '', unit: 'g', expiry_date: '' })
 
    useEffect(() => {
        if (authLoading) return

        let active = true
        Promise.resolve().then(() => {
            if (active) setLoading(true)
        })
        inventoryApi.list()
            .then(res => {
                if (active) setItems(res)
            })
            .catch((error) => {
                if (!active) return
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    setShowAccessDenied(true)
                    return
                }
                toast.error('Erreur chargement inventaire')
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [isAuthenticated, authLoading])

    const handleAdd = async () => {
        if (!form.ingredient_name.trim() || !form.quantity) return
        try {
            const item = await inventoryApi.add({ ingredient_name: form.ingredient_name.trim(), quantity: parseFloat(form.quantity), unit: form.unit, expiry_date: form.expiry_date || undefined })
            setItems(prev => [item, ...prev])
            setForm({ ingredient_name: '', quantity: '', unit: 'g', expiry_date: '' })
            setShowAdd(false)
            toast.success('Ajouté à l\'inventaire')
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                setShowAccessDenied(true)
                return
            }
            toast.error('Erreur ajout inventaire')
        }
    }

    const handleUpdate = async (id: string) => {
        try {
            await inventoryApi.update(id, { quantity: parseFloat(form.quantity), unit: form.unit })
            setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: parseFloat(form.quantity), unit: form.unit } : i))
            setEditId(null)
            toast.success('Mis à jour')
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                setShowAccessDenied(true)
                return
            }
            toast.error('Erreur mise à jour inventaire')
        }
    }

    const handleDelete = async (id: string) => {
        if (!confirm('Supprimer cet article ?')) return
        try {
            await inventoryApi.delete(id)
            setItems(prev => prev.filter(i => i.id !== id))
            toast.success('Supprimé')
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                setShowAccessDenied(true)
                return
            }
            toast.error('Erreur suppression inventaire')
        }
    }

    const UNITS = ['g', 'kg', 'ml', 'l', 'piece', 'cnt']

    const fmtQty = (qty: number, unit: string) => `${qty % 1 === 0 ? qty : qty.toFixed(1)} ${unit}`

    if (showAccessDenied) {
        return (
            <AccessDenied
                title="Acces a l'inventaire refuse"
                description="Vous devez creer un compte pour ajouter, modifier ou supprimer des elements de votre inventaire."
            />
        )
    }

    return (
        <div>
            <div className="page-header" style={{ justifyContent: 'space-between' }}>
                <h1>Inventaire</h1>
                <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(s => !s)}>
                    <Plus size={16} /> Ajouter
                </button>
            </div>

            {/* Add form */}
            {showAdd && (
                <div className="card fade-in" style={{ padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <h3 style={{ fontSize: '0.95rem' }}>Nouvel article</h3>
                    <IngredientAutocomplete
                        placeholder="Nom (ex: farine)"
                        value={form.ingredient_name}
                        onChange={(val, ing) => setForm(f => ({ 
                            ...f, 
                            ingredient_name: val, 
                            unit: ing?.preferred_unit || f.unit 
                        }))}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <input className="input" type="number" placeholder="Quantité" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
                        <select className="select" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </div>
                    <input className="input" type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} placeholder="Date de péremption (optionnel)" />
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-ghost" onClick={() => setShowAdd(false)} style={{ flex: 1 }}>Annuler</button>
                        <button className="btn btn-primary" onClick={handleAdd} style={{ flex: 2 }}>
                            <Check size={15} /> Confirmer
                        </button>
                    </div>
                </div>
            )}

            {loading && [1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 60, borderRadius: 12, marginBottom: 8 }} />)}

            {!loading && items.length === 0 && !showAdd && (
                <div className="empty-state">
                    <Package size={48} />
                    <h3>Inventaire vide</h3>
                    <p>Ajoutez les ingrédients que vous avez déjà dans votre cuisine</p>
                </div>
            )}

            {items.map((item, idx) => (
                <div
                    key={item.id}
                    className="list-item"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        animation: `slideUp 0.3s ease ${idx * 0.04}s both`,
                    }}
                >
                    {editId === item.id ? (
                        // Inline edit
                        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 6, alignItems: 'center' }}>
                            <input className="input input-compact" type="number" defaultValue={item.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
                            <select className="select input-compact" defaultValue={item.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                            <button className="btn btn-success btn-sm btn-icon" onClick={() => handleUpdate(item.id)}><Check size={14} /></button>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setEditId(null)}><X size={14} /></button>
                        </div>
                    ) : (
                        <>
                            <div className="list-item-info">
                                <div className="list-item-name">{item.ingredient_name}</div>
                                <div className="list-item-qty">{fmtQty(item.quantity, item.unit)}{item.expiry_date && ` · Exp: ${new Date(item.expiry_date).toLocaleDateString('fr-FR')}`}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                <button className="btn btn-ghost btn-sm btn-icon" onClick={() => { setEditId(item.id); setForm(f => ({ ...f, quantity: item.quantity.toString(), unit: item.unit })) }}>
                                    <Edit2 size={13} />
                                </button>
                                <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(item.id)}>
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
    )
}
